/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useQuery, useQueryClient } from "react-query";
import { selectConfigurationType, selectEventsData } from "../../../../Common/store/slices/common";
import {
  clearBundlesToCompare,
  saveApplicationDataDependencies,
  selectCurrentAxisId,
  selectDriveSynchronizedFilters,
  selectFiltersInit,
  selectPossibleFiltersValues,
  selectRetrofitConfiguration,
  selectSavedDriveDependenciesPayload,
  setAxisFilters,
  setDriveSynchronizedFilters,
  setSavedDriveDependenciesPayload,
  showSnackbar
} from "../../../../Retrofit/store/slices/retrofit";
import { logEvent, syncFiltersDependencies } from "../../../../Common/utils/api/api";
import { tData } from "../../../../Common/utils/translation/dataUtils";
import t from "../../../../Common/utils/translation/translator";
import prettyPrint from "../../../../Retrofit/utils/CorrespondingCrossComponent/prettyPrint";
import Button from "../../../../Common/components/Buttons/Button/Button";
import Spinner from "../../../../Common/components/Icons/Spinner/Spinner";
import LabelsPicker from "../../../../Common/components/Inputs/LabelsPicker/LabelsPicker/LabelsPicker";
import Select from "../../../../Common/components/Inputs/Select/Select";
import {
  formatConnectorType,
  formatDriveInterfaces,
  formatMotorEncoder,
  formatNbPhases,
  formatShaftEnd
} from "./utils";
import { getNominalToqueMultiplicator } from "../../../../Common/utils/func.util";

const DriveFilters = ({ closeTab, filters, filterToDisplay, shouldRerender, ...props }) => {
  const possibleDriveFiltersValues = useSelector(selectPossibleFiltersValues)?.drive;
  const configuration = useSelector(selectRetrofitConfiguration);
  const axisId = useSelector(selectCurrentAxisId);
  const eventsData = useSelector(selectEventsData);
  const configurationType = useSelector(selectConfigurationType);
  const synchronizedDriveFilters = useSelector(selectDriveSynchronizedFilters);
  const savedDependenciesPayload = useSelector(selectSavedDriveDependenciesPayload);
  const storedApplicationDataDependencies = useSelector(
    (state) => state.retrofit.configuration?.configData?.axes?.find((a) => a.id === axisId)?.axisState?.dependencies
  );
  const applicationDataFiltersValues = useSelector(selectPossibleFiltersValues)?.applicationData;
  const filtersInit = useSelector(selectFiltersInit);
  const dispatch = useDispatch();

  const [data, setData] = useState(null);
  const [motorFilters, setMotorFilters] = useState(null);
  const [gearboxFilters, setGearboxFilters] = useState(null);
  const [appDataFilters, setAppDataFilters] = useState(filters?.applicationDataFilter);
  const [currentAxis, setCurrentAxis] = useState(null);
  const [highlightErrors, setHighlightErrors] = useState(false);
  const [syncronizedFilters, setSyncronizedFilters] = useState(JSON.parse(JSON.stringify(synchronizedDriveFilters)));
  const [percentage, setPercentage] = useState(0);
  const [firstOccurence, setFirstOccurence] = useState(true);
  const [currentPhaseBeingSelected, setCurrentPhaseBeingSelected] = useState(null);
  const [loadingSetVoltage, setLoadingSetVoltage] = useState(false);
  const voltagesData = {
    "1 Phase": [100, 110, 120, 200, 220, 230, 240],
    "3 Phases": [200, 208, 220, 240, 380, 400, 480]
  };
  const [selectedLabelsInit, setSelectedLabelsInit] = useState(
    Object.keys(voltagesData)?.reduce((acc, phase) => {
      if (data?.nbPhases?.includes(phase)) {
        acc[phase] = data?.driveVoltagesSplitted;
      }
      return acc;
    }, {})
  );

  const chipFilters = ["communicationInterfaces", "type", "switchingFreq", "format", "nominalPower", "outputCurrents"];
  const queryClient = useQueryClient();

  const handleApplyFilters = () => {
    // check if driveInterface, nbPhases and driveVoltages are filled
    if (
      !data?.driveInterface ||
      data?.driveInterface?.length === 0 ||
      data?.driveInterface?.[0] === "" ||
      !data?.nbPhases ||
      data?.nbPhases?.length === 0 ||
      data?.nbPhases?.[0] === "" ||
      !data?.driveVoltages ||
      data?.driveVoltages?.length === 0 ||
      data?.driveVoltages?.[0] === ""
    ) {
      setHighlightErrors(true);
      dispatch(showSnackbar({ id: "MANDATORY_FIELDS" }));
      return;
    }
    // filters of the current axis
    const filters = {
      ...configuration.configData.axes.find((a) => a.id === axisId).configFilters,
      driveFilter: data
    };

    dispatch(setAxisFilters({ axisId, filters }));
    dispatch(clearBundlesToCompare());
    closeTab();
  };

  const updateLoaderProgress = (currentProgress, totalRequests) => {
    const percentageProgress = ((currentProgress / totalRequests) * 100).toFixed(2);
    setPercentage(percentageProgress);
  };

  const groupedCommunicationInterfaceValues = (values) => {
    const groupedValues = [];
    if (!Array.isArray(values)) {
      return groupedValues; // Return an empty array if values is not valid
    }
    values.forEach((value) => {
      switch (value) {
        case "Modbus RTU":
        case "Modbus TCP":
          groupedValues.push("Modbus");
          break;
        case "PTI":
        case "PTO":
        case "Digital I/O":
          groupedValues.push("Digital I/O");
          break;
        default:
          groupedValues.push(value);
      }
    });
    return [...new Set(groupedValues)];
  };

  const {
    refetch: execSyncFilters,
    isFetching: isFetchingSyncFilters,
    isLoading: isLoadingSyncFilters
  } = useQuery(
    "SYNC_DRIVE_FILTERS",
    async () => {
      const totalNbPhasesRequests = Object.keys(voltagesData)?.length;
      const totalChipFiltersRequests = chipFilters.length;
      const totalRequests = totalNbPhasesRequests + totalChipFiltersRequests + 1;

      let completedRequests = 0;

      const handleRequestCompletion = () => {
        completedRequests += 1;
        updateLoaderProgress(completedRequests, totalRequests);
      };

      const handlePercentageCompletion = () => {
        updateLoaderProgress(totalRequests, totalRequests);
        setTimeout(() => {
          updateLoaderProgress(0, totalRequests);
        }, 100);
      };

      try {
        const motorEncoderFormatted = formatMotorEncoder(motorFilters?.motorEncoder);
        const shaftEndFormatted = formatShaftEnd(motorFilters?.shaftEnd);
        const connectorTypeFormatted = formatConnectorType(motorFilters?.connectorType);
        const nbPhasesFormatted = formatNbPhases(data?.nbPhases);

        const commonPayload = {
          device: "ALL",
          criteria: {
            locale: window.msizerConfiguration?.locale || "en-WW",
            applicationData: {
              nominalSpeed: parseFloat(appDataFilters.nominalSpeed),
              nominalTorque: appDataFilters.nominalTorque
                ? {
                    min: parseFloat(appDataFilters?.nominalTorque),
                    max: parseFloat(appDataFilters?.nominalTorque) * getNominalToqueMultiplicator(configurationType)
                  }
                : null,
              inertia: parseFloat(appDataFilters.loadInertia),
              length: parseFloat(appDataFilters.maxLength),
              withGearbox: motorFilters?.withGearbox,
              nominalTorqueOfMotor: motorFilters?.nominalTorque,
              nominalSpeedOfMotor: motorFilters?.nominalSpeed
            },
            drive: {
              type: data?.driveType || [],
              communicationInterfaces: data?.driveInterface || [],
              outputCurrents: data?.outputCurrent || [],
              nominalPower: data?.nominalPower || [],
              switchingFreq: data?.switchingFreq || [],
              format: data?.format || [],
              nbPhases: nbPhasesFormatted,
              driveVoltages: data?.driveVoltages || []
            },
            motor: {
              type: motorFilters?.motorType || [],
              feedbackType: motorEncoderFormatted,
              connectorType: connectorTypeFormatted,
              ipProtection: motorFilters?.ipDegreeOfProtection || [],
              holdingBrake: motorFilters?.holdingBrake || [],
              shaftEnd: shaftEndFormatted,
              length: motorFilters?.length,
              flangeSize: motorFilters?.flangeSize
            },
            gearbox: {
              productOrComponentType: motorFilters?.withGearbox ? gearboxFilters?.productOrComponentType || [] : [],
              reductionRatio: motorFilters?.withGearbox ? gearboxFilters?.ratio || [] : [],
              torsionalBacklash: motorFilters?.withGearbox ? gearboxFilters?.torsionalBacklash : undefined,
              externalDiameter: motorFilters?.withGearbox ? gearboxFilters?.externalDiameter || [] : []
            }
          }
        };

        // application Data payload
        let payload = {
          ...commonPayload,
          criteria: {
            ...commonPayload.criteria,
            step: "bundle"
          }
        };

        let newApplicationDataFilters = storedApplicationDataDependencies;

        const previousPayload = currentAxis?.axisState?.savedApplicationDataPayload;

        // check if the new payload is different from the previous one (to avoid useless calls)
        if (JSON.stringify(payload) !== JSON.stringify(previousPayload)) {
          // sync application data filters
          newApplicationDataFilters = await syncFiltersDependencies(configurationType, payload);
          newApplicationDataFilters = newApplicationDataFilters?.applicationData;
          dispatch(
            saveApplicationDataDependencies({
              axisId,
              dependencies: newApplicationDataFilters,
              appDataPayload: payload
            })
          );
        }
        handleRequestCompletion();

        // payload used for drive filters
        payload = {
          ...commonPayload,
          criteria: {
            ...commonPayload.criteria,
            step: "drive"
          }
        };

        // if the filters are already synchronized, we don't need to do anything
        // if payload is the same than savedDependenciesPayload
        // we don't need to do anything

        if (savedDependenciesPayload && JSON.stringify(payload) === JSON.stringify(savedDependenciesPayload)) {
          console.log("payload is the same", payload);
          handlePercentageCompletion();
          return synchronizedDriveFilters;
        }

        const newFiltersPromise = syncFiltersDependencies(configurationType, payload);

        // make a request for each nbPhases (keys of the object voltagesData)
        const nbPhasesPayloadsResultsPromise = Promise.all(
          Object.keys(voltagesData)?.map(async (phase) => {
            const payloadNbPhases = {
              ...payload,
              nbPhasesStep: phase,
              criteria: {
                ...payload.criteria,
                drive: {
                  ...payload.criteria.drive,
                  nbPhases: formatNbPhases([phase]),
                  driveVoltages: []
                }
              }
            };
            const result = await syncFiltersDependencies(configurationType, payloadNbPhases);
            handleRequestCompletion();
            return { [phase]: result };
          })
        ).then((results) => {
          const res = {};
          results.forEach((result) => {
            const key = Object.keys(result)[0];
            res[key] = result[key]?.drive?.outputVoltages;
          });
          return res;
        });
        // console.log("nbPhasesPayloadsResultss", nbPhasesPayloadsResults);

        const chipFiltersPayloads = {};

        chipFilters.forEach(async (attribute) => {
          const payloadWithoutChipFilter = {
            ...payload,
            chipFilterName: attribute,
            criteria: {
              ...payload.criteria,
              drive: {
                ...payload.criteria.drive,
                [attribute]: []
              }
            }
          };
          // save the chipFiltersPayloads in the state in order to do multiple async calls at the same time
          chipFiltersPayloads[attribute] = payloadWithoutChipFilter;
        });

        const chipFiltersPayloadsResultsPromise = Promise.all(
          Object.keys(chipFiltersPayloads).map(async (key) => {
            const result = await syncFiltersDependencies(configurationType, chipFiltersPayloads[key]);
            handleRequestCompletion();
            return { [key]: result };
          })
        ).then((results) => {
          const res = {};
          results.forEach((result) => {
            const key = Object.keys(result)[0];
            res[key] = result[key]?.drive?.[key];
          });
          return res;
        });

        // Use Promise.all to execute both async operations concurrently
        const [newFilters, nbPhasesPayloadsResults, chipFiltersPayloadsResults] = await Promise.all([
          newFiltersPromise,
          nbPhasesPayloadsResultsPromise,
          chipFiltersPayloadsResultsPromise
        ]);

        const synchronizedFiltersCopy = newFilters?.drive;

        setSyncronizedFilters({
          ...synchronizedFiltersCopy,
          ...chipFiltersPayloadsResults,
          voltagesPerPhase: nbPhasesPayloadsResults
        });

        dispatch(
          setDriveSynchronizedFilters({
            ...synchronizedFiltersCopy,
            voltagesPerPhase: nbPhasesPayloadsResults
          })
        );
        dispatch(setSavedDriveDependenciesPayload(payload));
        handlePercentageCompletion();
        return newFilters;
      } catch (error) {
        // dispatch(showMinimalPopup(`Filters dependencies error`));
        dispatch(showSnackbar({ id: "ERROR_FILTERS" }));
        logEvent(eventsData.locale, eventsData.types.errorFilters, eventsData.tags.none, configuration?.configId);
        console.error("ERR", error.response?.status);
        handlePercentageCompletion();
      }
      return undefined;
    },
    {
      enabled: false
    }
  );

  const handleClearFilters = () => {
    console.log("clear drive filters");
    // empty all filters
    setData({
      ...filtersInit.driveFilter,
      driveType: [],
      driveInterface: [],
      outputCurrent: [],
      nominalPower: [],
      switchingFreq: [],
      format: [],
      nbPhases: []
    });
  };

  const groupDriveTypeValues = (values) => {
    // group LXM28A, LXM28E, LXM28S to LXM28
    // and group LXM32A, LXM32C, LXM32M, LXM32S to LXM32
    // don't group other values
    const groupedValues = [];
    values.forEach((value) => {
      switch (value) {
        case "LXM28A":
        case "LXM28E":
        case "LXM28S":
          groupedValues.push("LXM28");
          break;
        case "LXM18E":
        case "LXM18M":
        case "LXM18P":
          groupedValues.push("LXM18");
          break;
        case "LXM32A":
        case "LXM32C":
        case "LXM32M":
        case "LXM32S":
          groupedValues.push("LXM32");
          break;

        default:
          groupedValues.push(value);
      }
    });

    // return unique values of the array
    return [...new Set(groupedValues)];
  };

  const handleSetVoltage = (voltageValue, phase) => {
    /*
        example : val = [220]
        driveVoltage should be ["200...240 V", "220 V", "208...480 V"]
        (220 is between 200 and 240..etc)

        set the nbPhases to the given value if the voltage is not empty
      */
    setLoadingSetVoltage(true);

    const driveVoltage =
      // syncronizedFilters?.outputVoltages?.filter(
      possibleDriveFiltersValues?.driveVoltages?.filter((voltage) => {
        if (voltage.includes("...")) {
          const values = voltage.split("...").map((v) => parseInt(v, 10));
          return values[0] <= voltageValue[0] && voltageValue[0] <= values[1];
        }
        return parseInt(voltage, 10) === voltageValue[0];
      });

    // set the data only if it's different from the previous value
    const newData = {
      ...data,
      nbPhases: voltageValue?.length ? [phase] : [],
      driveVoltagesSplitted: voltageValue,
      driveVoltages: driveVoltage
    };

    if (JSON.stringify(newData) !== JSON.stringify(data)) {
      console.log("handleSetVoltage", voltageValue, "=>", driveVoltage, phase);
      setData({
        ...data,
        nbPhases: voltageValue?.length ? [phase] : [],
        driveVoltagesSplitted: voltageValue,
        driveVoltages: driveVoltage
      });
    }

    const newSelectedLabelsInit = {
      [phase]: voltageValue
    };
    console.log("newSelectedLabelsInit", newSelectedLabelsInit);
    setSelectedLabelsInit(newSelectedLabelsInit);
    setTimeout(() => {
      setLoadingSetVoltage(false);
    }, 100);
  };

  const sortDriveVoltages = (driveVoltages) => {
    const ranges = [];
    const singleValues = [];

    // For each value check if it contains "..." if yes it get pushed to the "ranges" array, if not it get pushed to the "singleValues" array. Every NaN elements are removed.
    driveVoltages?.forEach((voltage) => {
      if (voltage.includes("...")) {
        const rangeValues = voltage
          .split("...")
          .map((part) => parseInt(part.trim(), 10))
          .filter((num) => !Number.isNaN(num));
        ranges.push(...rangeValues);
      } else {
        const num = parseInt(voltage.trim(), 10);
        if (!Number.isNaN(num)) {
          singleValues.push(num);
        }
      }
    });

    ranges.sort((a, b) => a - b);

    // Return an object with the min and max of the ranges array and the array of single values.
    return {
      min: ranges[0],
      max: ranges[ranges.length - 1],
      singleValues
    };
  };

  const supplyVoltageFilter = (
    <div className="space-y-2 ml-1">
      <span className="text-left font-semibold">
        {t("msizer.ui.app_settings.select_voltage")}
        <span className="text-red-600 ml-1">*</span>
      </span>
      {Object.entries(voltagesData).map(([phase, values]) => (
        <div onClick={() => setCurrentPhaseBeingSelected(phase)}>
          <LabelsPicker
            idLabel={phase}
            label={tData("drive", "nb_phases", phase)}
            required={!filterToDisplay}
            singleChoice
            large
            cols={3}
            options={values?.map((voltage) => ({
              label: `${voltage} V`,
              key: voltage
            }))}
            onChange={(voltage) => {
              if (currentPhaseBeingSelected === phase) {
                handleSetVoltage(voltage, phase);
              }
            }}
            selectedLabelsInit={
              selectedLabelsInit[phase] || []
              // data?.nbPhases?.includes(phase) ? data?.driveVoltagesSplitted : []
            }
            changeWithInit={loadingSetVoltage}
            disabledLabels={values?.filter((val) => {
              // Check if the value is a single value, if not check if it's out of range
              const sortedDriveVoltages = sortDriveVoltages(synchronizedDriveFilters?.voltagesPerPhase?.[phase]);

              if (sortedDriveVoltages.singleValues?.includes(val)) return false;

              if (!sortedDriveVoltages.min && !sortedDriveVoltages.max) {
                return true;
              }

              return val < sortedDriveVoltages.min || val > sortedDriveVoltages.max || !syncronizedFilters;
            })}
            error={
              highlightErrors &&
              (!data?.driveVoltages || !data?.driveVoltages?.length || data?.driveVoltages?.[0] === "")
            }
          />
        </div>
      ))}
    </div>
  );

  const communicationInterfaceFilterWithLabelPicker = (
    <LabelsPicker
      singleChoice
      label={!filterToDisplay && t("msizer.ui.drive_filters.communication_interfaces")}
      required={!filterToDisplay}
      error={
        highlightErrors && (!data?.driveInterface || !data?.driveInterface?.length || data?.driveInterface?.[0] === "")
      }
      cols={3}
      // order options alphabetically
      options={[
        ...groupedCommunicationInterfaceValues(formatDriveInterfaces(possibleDriveFiltersValues?.driveInterface) || [])
          .filter((d) => d !== null)
          ?.sort()
          .map((val) => ({
            label: tData("drive", "communication_interfaces", val),
            key: val
          }))
      ]}
      selectedLabelsInit={data?.driveInterface}
      onChange={(val) => {
        const selectedValues = val.includes("Modbus")
          ? ["Modbus RTU", "Modbus TCP", "Modbus"]
          : val.includes("Digital I/O")
          ? ["PTI", "PTO", "Digital I/O"]
          : val;
        setData({ ...data, driveInterface: selectedValues });
      }}
      changeWithInit
      disabledLabels={[
        ...groupedCommunicationInterfaceValues(formatDriveInterfaces(possibleDriveFiltersValues?.driveInterface))
      ]?.filter(
        (val) => ![...groupedCommunicationInterfaceValues(syncronizedFilters?.communicationInterfaces)].includes(val)
      )}
    />
  );

  if (filterToDisplay === "voltage") {
    // return nbPhasesFilter and supplyVoltageFilter
    return { supplyVoltageFilter };
  }

  if (filterToDisplay === "communicationInterface") {
    return (
      <div className="relative">
        <div
          className={`bg-gray-50 backdrop-blur-xs bg-opacity-50 h-full w-full top-0 left-0 absolute flex items-center justify-center z-20 ${
            !(isFetchingSyncFilters || isLoadingSyncFilters) && "hidden"
          }`}
        >
          <Spinner />
        </div>
        <div>
          <span className="text-left font-semibold ml-1">
            {t("msizer.ui.app_settings.select_communication")}
            <span className="text-red-600 ml-1">*</span>
          </span>
          {communicationInterfaceFilterWithLabelPicker}
        </div>
      </div>
    );
  }

  if (filterToDisplay === "applicationSettings") {
    return (
      <div className="relative">
        <div
          className={`bg-gray-50 backdrop-blur-xs bg-opacity-50 h-full w-full top-0 left-0 absolute flex items-center justify-center z-20 ${
            !(isFetchingSyncFilters || isLoadingSyncFilters) && "hidden"
          }`}
        >
          <Spinner />
        </div>
        <div className="space-y-4">
          <div>{supplyVoltageFilter}</div>
          <div>
            <span className="text-left font-semibold ml-1">
              {t("msizer.ui.app_settings.select_communication")}
              <span className="text-red-600 ml-1">*</span>
            </span>
            {communicationInterfaceFilterWithLabelPicker}
          </div>
        </div>
      </div>
    );
  }

  useEffect(() => {
    const axisFromConfiguration = configuration?.configData?.axes?.find((a) => a.id === axisId);
    setCurrentAxis(axisFromConfiguration);
    setMotorFilters(axisFromConfiguration?.configFilters?.motorFilter);
    setGearboxFilters(axisFromConfiguration?.configFilters?.gearboxFilter);
    setAppDataFilters(axisFromConfiguration?.configFilters?.applicationDataFilter);
  }, [configuration]);

  useEffect(() => {
    const axisFromConfiguration = configuration?.configData.axes.find((a) => a.id === axisId);
    setData(axisFromConfiguration?.configFilters?.driveFilter);
  }, [shouldRerender]);

  useEffect(() => {
    if (data && !firstOccurence) {
      queryClient.cancelQueries("SYNC_MOTOR_FILTERS");
      queryClient.cancelQueries("SYNC_GEARBOX_FILTERS");
      execSyncFilters();

      const filters = {
        ...configuration.configData.axes.find((a) => a.id === axisId).configFilters,
        driveFilter: data
      };

      dispatch(setAxisFilters({ axisId, filters }));
    } else {
      setFirstOccurence(false);
    }
  }, [data, motorFilters?.withGearbox]);

  useEffect(() => {
    setLoadingSetVoltage(true);
    setSelectedLabelsInit(
      Object.keys(voltagesData)?.reduce((acc, phase) => {
        if (data?.nbPhases?.includes(phase)) {
          acc[phase] = data?.driveVoltagesSplitted;
        }
        return acc;
      }, {})
    );
    setTimeout(() => {
      setLoadingSetVoltage(false);
    }, 100);
  }, [data]);

  return (
    <>
      <div className="DriverFilter relative" {...props}>
        <div
          // use the percentage for the width
          className="h-1 rounded bg-primary transition-all duration-100"
          style={{
            width: `${percentage}%`
          }}
        />
        <div
          className={`bg-gray-50 backdrop-blur-xs bg-opacity-50 h-full w-full top-0 left-0 absolute flex items-center justify-center z-20 ${
            !(isFetchingSyncFilters || isLoadingSyncFilters || !data) && "hidden"
          }`}
        >
          <Spinner />
        </div>
        <div className="md:flex justify-end hidden">
          <span
            onClick={handleClearFilters}
            className="text-secondary text-sm px-2 py-1 cursor-pointer hover:underline select-none"
          >
            {t("msizer.ui.filters.clear")}
          </span>
        </div>
        <div className="md:flex space-x-2 md:divide-x-2 md:divide-gray-100 md:divide-solid">
          <div className="flex-1 p-2 space-y-4">
            {supplyVoltageFilter}
            <div className="h-fit md:h-40">{communicationInterfaceFilterWithLabelPicker}</div>
          </div>
          <div className="flex-1 p-2 space-y-4">
            <LabelsPicker
              idLabel="drive-type"
              label={t("msizer.ui.drive_filters.drive_type")}
              singleChoice
              error={highlightErrors && (!data?.driveType || data?.driveType?.length === 0)}
              cols={3}
              options={[
                ...groupDriveTypeValues(possibleDriveFiltersValues?.driveType || [])
                  .filter((d) => d !== null)
                  ?.sort()
                  .map((type) => ({
                    label: tData("drive", "drive_type", type),
                    key: type
                  }))
              ]}
              selectedLabelsInit={data?.driveType}
              onChange={(val) => {
                setData({ ...data, driveType: val });
              }}
              disabledLabels={[...groupDriveTypeValues(possibleDriveFiltersValues?.driveType || [])].filter(
                (val) => ![...groupDriveTypeValues(syncronizedFilters?.type || [])].includes(val) || !syncronizedFilters
              )}
              changeWithInit
            />
            <LabelsPicker
              idLabel="drive-frequency"
              label={t("msizer.ui.drive_filters.switching_frequency")}
              cols={2}
              options={[
                ...possibleDriveFiltersValues.switchingFreq
                  .filter((val) => val !== null)
                  ?.sort((a, b) => a - b)
                  .map((val) => ({
                    label: prettyPrint("switchingFreq", val),
                    key: val
                  }))
              ]}
              selectedLabelsInit={data?.switchingFreq}
              onChange={(val) => {
                setData({ ...data, switchingFreq: val });
              }}
              changeWithInit
              disabledLabels={possibleDriveFiltersValues.switchingFreq.filter(
                (val) => !syncronizedFilters?.switchingFreq?.includes(val) || !syncronizedFilters
              )}
            />
            <Select
              id="filter-drive-nominalPower"
              label={t("msizer.ui.drive_filters.nominal_power")}
              placeholder={t("msizer.ui.drive_filters.nominal_power.placeholder")}
              unit={t("msizer.ui.unit.kw")}
              options={[
                {
                  label: t("msizer.ui.drive_filters.nominal_power.placeholder"),
                  key: ""
                },
                ...(possibleDriveFiltersValues?.nominalPower || [])
                  .map((element) => ({
                    label: prettyPrint("nominalPower", element),
                    key: element,
                    disabled: !syncronizedFilters?.nominalPower?.includes(element)
                  }))
                  .sort((a, b) => a.key - b.key)
              ]}
              initValue={data?.nominalPower}
              onSelect={(type) => {
                console.log(type);

                setData({
                  ...data,
                  nominalPower: type === "" ? undefined : [type]
                });
              }}
              changeWithInit
            />
          </div>

          <div className="flex-1 p-2 space-y-4">
            <Select
              label={t("msizer.ui.drive_filters.output_current")}
              placeholder={t("msizer.ui.drive_filters.output_current.placeholder")}
              unit={t("msizer.ui.unit.a")}
              options={[
                {
                  label: t("msizer.ui.drive_filters.output_current.placeholder"),
                  key: ""
                },
                ...(possibleDriveFiltersValues?.outputCurrent || [])
                  .map((element) => ({
                    label: prettyPrint("outputCurrents", element),
                    value: element,
                    disabled: !syncronizedFilters?.outputCurrents?.includes(element.toString())
                  }))
                  .sort((a, b) => a.value - b.value)
              ]}
              initValue={data?.outputCurrent}
              onSelect={(type) => {
                console.log(type);
                setData({
                  ...data,
                  outputCurrent: type === "" ? undefined : [type]
                });
              }}
              changeWithInit
            />

            <LabelsPicker
              label={t("msizer.ui.drive_filters.format")}
              cols={2}
              options={[
                ...possibleDriveFiltersValues.format
                  .filter((val) => val !== null)
                  ?.sort()
                  .map((val) => ({
                    label: tData("drive", "format", val),
                    key: val
                  }))
              ]}
              selectedLabelsInit={data?.format}
              onChange={(val) => {
                setData({ ...data, format: val });
              }}
              changeWithInit
              disabledLabels={possibleDriveFiltersValues?.format?.filter(
                (val) => !syncronizedFilters?.format?.includes(val) || !syncronizedFilters
              )}
            />
          </div>
        </div>
      </div>
      <div className="flex justify-center space-x-2 pt-2">
        <div className="flex-1 px-2" />
        <div className="flex-1 px-2 flex gap-2 md:gap-0 justify-center">
          <qds-button
            id="select-filters-button-apply"
            onClick={handleApplyFilters}
            {...(!data?.driveInterface ||
            data?.driveInterface?.length === 0 ||
            data?.driveInterface?.[0] === "" ||
            !data?.nbPhases ||
            data?.nbPhases?.length === 0 ||
            data?.nbPhases?.[0] === "" ||
            !data?.driveVoltages ||
            data?.driveVoltages?.length === 0 ||
            data?.driveVoltages?.[0] === ""
              ? { disabled: "" }
              : {})}
            text={t("msizer.ui.popup.button.apply")}
          ></qds-button>
          <Button onClick={handleClearFilters} border="blue" textColor="blue" large classes="md:hidden">
            {t("msizer.ui.filters.clear")}
          </Button>
        </div>
        <div className="flex-1 px-2" />
      </div>
    </>
  );
};

DriveFilters.propTypes = {
  closeTab: PropTypes.func,
  filterToDisplay: PropTypes.string,
  shouldRerender: PropTypes.bool
};

DriveFilters.defaultProps = {
  closeTab: () => {},
  filterToDisplay: null,
  shouldRerender: false
};

export default DriveFilters;
